'use client';

import React, { createContext, useContext } from 'react';

import { TimeZone } from '../../helpers/locale-helper';

/**
 * The zone every formatted date in the tree should be read in.
 *
 * An operator app shows one business's data, and that business keeps its own clock — a cinema's
 * programme is written in cinema time. Formatting with the browser's zone instead makes the same
 * screening read 03:47 to someone on UTC and 04:47 on the public website, and the operator has no
 * way to tell which is the real start.
 *
 * Provided once at the app root so components can format without each re-deriving the zone and
 * without threading it through props. {@link useFormat} reads it as its default, so most callers
 * never mention a zone at all.
 */
const TimeZoneContext = createContext<TimeZone | undefined>(undefined);

/**
 * The zone from the nearest provider, or `undefined` when there is none — consumers decide their
 * own fallback rather than silently inheriting one from here.
 */
export function useTimeZone(): TimeZone | undefined {
    return useContext(TimeZoneContext);
}

export function TimeZoneProvider({
    timeZone,
    children
}: { timeZone: TimeZone | undefined } & React.PropsWithChildren): React.JSX.Element {
    return <TimeZoneContext.Provider value={timeZone}>{children}</TimeZoneContext.Provider>;
}

export default TimeZoneProvider;
